import React, { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { mixpanelTrack } from '@providers/Mixpanel';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import ProgressIndicator from '@components/ProgressIndicator';
import Spacer from '@components/Spacer';
import { DocumentAccessContext, DocumentAccessGate } from '@providers/DocumentAccess';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import * as ApiModels from '@typings/api-models';
import * as format from '@format';
import removeNullish from '@helpers/removeNullish';
import TeamsContext from '@providers/Teams/TeamsContext';
import ConfirmationModal from '@modals/ConfirmationModal';
import DashboardContext from '@layouts/DashboardLayout/DashboardContext';
import HandCoins from '@components/Icons/HandCoins';
import FilePdf from '@components/Icons/FilePdf';
import DocumentPreview from '@/components/Document/DocumentPreview';
import { useToast } from '@hooks';
import MainPanelSubLayout from '@layouts/MainPanelSubLayout';
import userWorkflowToContext from '@helpers/userWorkflowToContext';
import UserProfileContext from '@layouts/UserProfileLayout/UserProfileContext';
import PayInvoiceOutsideNoodleModal from '@modals/PayInvoiceOutsideNoodleModal';
import ConfidoPaymentModal from '@modals/ConfidoPaymentModal';
import CurrencyCircleDollar from '@components/Icons/CurrencyCircleDollar';
import PaymentPlanModal from '@modals/PaymentPlanModal';
import { useFeatureFlag } from '@providers/FeatureFlags';
import ArrowClockwise from '@components/Icons/ArrowClockwise';
import ArrowCounterClockwise from '@components/Icons/ArrowCounterClockwise';
import RadioButton from '@components/Icons/RadioButton';
import CheckCircle from '@components/Icons/CheckCircle';
import WarningCircle from '@components/Icons/WarningCircle';
import XCircle from '@components/Icons/XCircle';
import Clock from '@components/Icons/Clock';
import CancelPaymentPlanModal from '@modals/CancelPaymentPlanModal';
import Button from '@/components/DesignLibrary/Button';
import { DEFAULT_CURRENCY } from '@typings/common';
import classNames from 'classnames';
import { format as formatDate } from 'date-fns';

import { useEventsContext } from '@/providers/Events';
import useSocketContext from '@/providers/Socket/useSocketContext';
import { SocketMessageType } from '@/providers/Socket';
import s from './InvoicePreview.module.scss';
import InvoicePaymentsTable from './PaymentsTable';
import Banner from './Banner';
import PaymentProgress from './PaymentProgress';
import EditInvoiceModal from './EditInvoiceModal';
import EditInvoiceConfirmationModal from './EditInvoiceConfirmationModal';

type Props = {
  invoiceId: string;
  creatorSlug: string;
  replaceByTypeAndId?: (type:string,id:string) => void;
};

const InvoicePreview: React.FC<Props> = ({ invoiceId, creatorSlug, replaceByTypeAndId }) => {
  const { emit } = useEventsContext();
  const {joinChannels} = useSocketContext();
  const { data: invoice, getData: getInvoiceFn } = useNoodleApi(tsClient.invoices.getInvoice, { toastOnError: true });
  const { getData: voidInvoiceFn } = useNoodleApi(tsClient.invoices.voidInvoice);
  const { accessToken } = useContext(DocumentAccessContext);
  const { currentTeam, creatorId } = useContext(TeamsContext);
  const { isInDashboard } = useContext(DashboardContext);
  const { isInUserProfile } = useContext(UserProfileContext);
  const [isPayOutsideModalOpen, setPayOutsideModalOpen] = useState(false);
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isEditConfirmationModalOpen, setEditConfirmationModalOpen] = useState(false);
  const [isEditInvoiceModalOpen, setIsEditInvoiceModalOpen] = useState(false);
  const [isConfidoPaymentModalOpen, setConfidoPaymentModalOpen] = useState(false);
  const [isPaymentPlanModalOpen, setPaymentPlanModalOpen] = useState(false);
  const [isCancelPaymentPlanModalOpen, setCancelPaymentPlanModalOpen] = useState(false);
  const [isInvoiceView, setIsInvoiceView] = useState(false);

  const useConfidoCart = useFeatureFlag('confido-cart');

  const router = useRouter();

  const {
    data: userWorkflow,
    fetchingState: userWorkflowFetchingState,
    getData: getUserWorkflowFn,
  } = useNoodleApi(tsClient.workflows.getUserWorkflowById);

  const { data: payments, getData: getPaymentsFn } = useNoodleApi(tsClient.invoices.getInvoicePayments);

  const { getData: updateInvoiceFn } = useNoodleApi(tsClient.invoices.updateInvoiceWithVersion, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Invoice edited successfully!'],
  });

  useEffect(() => {
    mixpanelTrack('InvoicePreview - viewed', { creatorSlug, invoiceId });
  }, [creatorSlug, invoiceId]);

  useEffect(() => {
    getInvoiceFn({ id: invoiceId });
    getPaymentsFn({ id: invoiceId, page: 1, perPage: 1000 });
  }, [getPaymentsFn, getInvoiceFn, invoiceId]);

  useEffect(() => {
    if (invoice?.userWorkflowId) {
      getUserWorkflowFn({ userWorkflowId: invoice.userWorkflowId });
    }
  }, [invoice, getUserWorkflowFn]);

  const payInvoice = (onBehalfOfClient: boolean): void => {
    if (creatorSlug && invoice) {
      if (invoice.confidoPaymentLink && !useConfidoCart) {
        setConfidoPaymentModalOpen(true);
      } else {
        router.push(
          getUrl(IDENTIFIERS.PAY_INVOICE, {
            creatorSlug,
            invoiceId,
            onBehalfOfPersonId: onBehalfOfClient ? invoice.personId : null,
          }),
        );
      }
    }
  };

  const voidInvoice = async (): Promise<void> => {
    await voidInvoiceFn({ id: invoiceId });
    await getInvoiceFn({ id: invoiceId });
    setConfirmationModalOpen(false);
  };

  const refetchData = async (): Promise<void> => {
    await Promise.all([getInvoiceFn({ id: invoiceId }), getPaymentsFn({ id: invoiceId, page: 1, perPage: 1000 })]);
  };

  if (!invoice) {
    return (
      <>
        <Spacer size={80} />
        <ProgressIndicator isPage isCentered />
      </>
    );
  }

  const canEdit = currentTeam
    && (invoice.status === ApiModels.INVOICE_STATUS.IN_PROGRESS || invoice.status === ApiModels.INVOICE_STATUS.PAYMENT_FAILED);
  const canVoid = canEdit;

  const showPayAction = invoice.canBePaid && invoice.hasPayPermission;
  const showPayOutsideAction = invoice.canBePaid && isInDashboard && creatorId === invoice.creatorId;
  const showPaymentPlanAction = invoice.isPartialPaymentEnabled && invoice.canBePaid;

  const actions = [
    invoice.document
      ? {
        icon: isInvoiceView ? XCircle : FilePdf,
        isSecondary: true,
        label: `${isInvoiceView ? 'Hide' : 'Show'} Invoice`,
        onClick: () => setIsInvoiceView(prevState => !prevState),
      }
      : null,
    canVoid && isInDashboard
      ? {
        isDestructive: true,
        isSecondary: true,
        label: 'Void Invoice',
        onClick: () => setConfirmationModalOpen(true),
      }
      : null,
    canEdit && isInDashboard
      ? {
        isDestructive: false,
        isSecondary: true,
        label: 'Make a correction',
        onClick: () => setEditConfirmationModalOpen(true),
      }
      : null,
    !isInDashboard && !isInUserProfile
      ? {
        href: getUrl(IDENTIFIERS.USER_PROFILE, {
          creatorSlug,
          tab: invoice.userWorkflowId ? 'activity' : 'chat',
          userWorkflowId: invoice.userWorkflowId || undefined,
        }),
        label: 'Back to profile',
      }
      : null,
  ].filter(removeNullish);

  const isVoided = invoice.status === ApiModels.INVOICE_STATUS.VOIDED;
  const isPaid = invoice.status === ApiModels.INVOICE_STATUS.PAID;
  const isSkipped = invoice.status === ApiModels.INVOICE_STATUS.SKIPPED;
  const isPaymentFailed = !isSkipped && invoice.status === ApiModels.INVOICE_STATUS.PAYMENT_FAILED;

  const editInvoiceModalToggle = ():void => {
    setIsEditInvoiceModalOpen(!isEditInvoiceModalOpen);
  };

  const handleSubmitInvoice = async (invoiceData: {
    invoiceData: Pick<
    ApiModels.InvoiceTemplate,
    | 'confidoBankAccountId'
    | 'isAchPaymentEnabled'
    | 'isCreditCardPaymentEnabled'
    | 'isDebitCardPaymentEnabled'
    | 'isDebitCardPaymentRequiredForCustomer'
    | 'isPartialPaymentEnabled'
    | 'passProcessingFeesToCustomer'
    | 'secondaryStripeAccountId'
    | 'title'>,
    lineItems:Omit<ApiModels.InvoiceLineItem, 'id' | 'priceId' | 'creatorId' | 'invoiceId'>[]
  }): Promise<void> => {
    const payload = {
      ...invoiceData?.invoiceData,
      lineItems: invoiceData?.lineItems?.map((item) => {
        const temp:{
          id?: string;
          item: string;
          price: number | null;
          description: string | null;
          quantity: number;
          customTermsVariableName: string | null;
        } = {
          ...item,
        };
        delete temp.id;
        return temp;
      }),
      previousVersionInvoiceIds: invoice?.previousVersionInvoiceIds ?? null,
      userWorkflowId: userWorkflow?.id ?? '',
    };
    const newInvoice = await updateInvoiceFn({
      ...payload,
      id: invoice?.id,
    });
    if (newInvoice?.data) emit('refreshUserWorkflowList', {});
    joinChannels([SocketMessageType.USER_WORKFLOW_UPDATED]);
    if(newInvoice?.data && replaceByTypeAndId) {
      replaceByTypeAndId('invoice',newInvoice?.data);
    }
    setIsEditInvoiceModalOpen(false);
  };

  return (
    <>
      <Head>
        <title>{invoice.document?.fileName || 'Invoice'}</title>
      </Head>
      <MainPanelSubLayout

        homeTabTitle='Invoice'
        header={{
          actions,
          description: invoice?.document ? null : 'Invoice document is getting generated...',
          title: invoice.title || invoice.document?.fileName || 'Invoice',
        }}
        pageContext={{ isFetching: userWorkflowFetchingState.isFetching, items: userWorkflowToContext(userWorkflow) }}
      >

        <div className={s.container}>
          <div className={s.content}>
            {isInvoiceView && invoice.document
              ? <DocumentAccessGate
                documentCreatorId={invoice.document.creatorId}
                documentPersonId={invoice.document.personId}
                isCollaborator={invoice.document.isCollaborator || invoice.hasInvoiceViewPermission}
                isCenteredVertically
              >
                <DocumentPreview
                  document={invoice.document}
                  accessToken={accessToken}
                  canDownload={!isVoided || !isInDashboard}
                  isFaded={isVoided}
                />
              </DocumentAccessGate>
              : <>
                <div className={classNames(s.header, !isInDashboard && s.headerClient)}>
                  <div className={s.headerMain}>
                    <p className="caption">Invoice total amount</p>
                    <p className="heading-md">{format.price.withCurrency(invoice.amountTotal, invoice.currency || DEFAULT_CURRENCY)}</p>
                    {!isInDashboard && (
                      <div className={s.headerMainBanner}>
                        <Banner isSkipped={isSkipped} isVoided={isVoided} isPaid={isPaid} isPaymentFailed={isPaymentFailed} />
                      </div>
                    )}
                    {(showPayAction || showPaymentPlanAction || showPayOutsideAction)
                      && (isInDashboard || !invoice.paymentPlan) && <div className={s.headerButtons}>
                      {showPayAction && (
                        <Button size="sm" variant="primary" onClick={() => payInvoice(isInDashboard)}>
                          <HandCoins size={16} /> {isInDashboard ? 'Make payment on behalf of client' : 'Make payment'}
                        </Button>
                      )}
                      {showPaymentPlanAction && (
                        <Button size="sm" variant="secondary" onClick={() => setPaymentPlanModalOpen(true)}>
                          {invoice.paymentPlan ? <ArrowClockwise size={16} /> : <CurrencyCircleDollar size={16} />}{' '}
                          {invoice.paymentPlan ? 'Modify payment plan' : 'Set up payment plan'}
                        </Button>
                      )}
                      {showPayOutsideAction && (
                        <Button size="sm" variant="secondary" onClick={() => setPayOutsideModalOpen(true)}>
                          Record a payment outside of Glade
                        </Button>
                      )}
                    </div>}
                  </div>
                  {!(isSkipped || isVoided || isPaid || isPaymentFailed) && isInDashboard && (
                    <PaymentProgress
                      amountToBeRefunded={invoice.amountToBeRefunded}
                      amountDue={invoice.amountDue}
                      amountPaid={invoice.amountPaid}
                      amountTotal={invoice.amountTotal}
                      amountPending={invoice.amountPending}
                      currency={invoice.currency}
                    />
                  )}
                  {isInDashboard && (
                    <Banner isSkipped={isSkipped} isVoided={isVoided} isPaid={isPaid} isPaymentFailed={isPaymentFailed} />
                  )}
                </div>
                {payments?.items && isInDashboard && (
                  <InvoicePaymentsTable
                    refetchData={refetchData}
                    invoice = {invoice}
                    payments={payments.items}
                    paymentPlan={invoice.paymentPlan}
                    currency={invoice.currency}
                  />
                )}
                {payments?.items && !isInDashboard && (
                  <div className={s.list}>
                    {invoice.paymentPlan?.nextPaymentDate && (
                      <div className={s.listUpcoming}>
                        <div className={s.listUpcomingDate}>
                          <div className={s.listUpcomingDateBadge}>
                            <small className='caption'>{formatDate(new Date(invoice.paymentPlan.nextPaymentDate), 'MMM')}</small>
                            <b className='body-md-bold'>{formatDate(new Date(invoice.paymentPlan.nextPaymentDate), 'd')}</b>
                          </div>
                          <div>
                            <p className='body-md-bold'>{format.price.withCurrency(invoice.paymentPlan.amount, invoice.currency)}</p>
                            <p className='caption'>Next payment in {format.datetimeLocalTZ.timeSince({ date: invoice.paymentPlan.nextPaymentDate })}</p>
                          </div>
                        </div>
                        <div className={s.listButtons}>
                          {showPaymentPlanAction && (
                            <Button size="sm" variant="secondary" onClick={() => setPaymentPlanModalOpen(true)}>
                              <ArrowClockwise size={16} /> Modify payment plan
                            </Button>
                          )}
                          {showPayAction && (
                            <Button size="sm" variant="primary" onClick={() => payInvoice(isInDashboard)}>
                              <HandCoins size={16} /> Make additional payment
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                    {!(isSkipped || isVoided || isPaid || isPaymentFailed) && invoice.paymentPlan && (
                      <PaymentProgress
                        amountToBeRefunded={invoice.amountToBeRefunded}
                        amountDue={invoice.amountDue}
                        amountPaid={invoice.amountPaid}
                        amountTotal={invoice.amountTotal}
                        amountPending={invoice.amountPending}
                        currency={invoice.currency}
                        noBorder
                      />
                    )}
                    {(payments.items.length > 0 || invoice.paymentPlan) && <ul>
                      {invoice.paymentPlan?.nextPaymentDate && <li>
                        <RadioButton size={14} weight='fill' color="var(--color-gray-100)" />
                        <div>
                          <p className="caption-bold" style={{ color: 'var(--color-gray-75)' }}>{format.price.withCurrency(invoice.paymentPlan.amount, invoice.currency)}</p>
                        </div>
                        <p className="caption"><span>{format.datetimeLocalTZ.withYear(invoice.paymentPlan.nextPaymentDate)}</span></p>
                      </li>}
                      {payments.items.length > 0 && <>
                        {payments.items.map(payment => {
                          if (payment.type === 'cancel-event') {
                            return null;
                          }
                          return (
                            <li key={payment.id}>
                              {payment.paymentStatus === 'succeeded' && payment.amountRefunded === 0 && <CheckCircle size={14} weight='fill' color="var(--color-success)" />}
                              {payment.paymentStatus === 'requires_payment_method' && <WarningCircle size={14} weight='fill' color="var(--color-warning)" />}
                              {(payment.paymentStatus === 'failed' || payment.paymentStatus === 'voided') && payment.amountRefunded === 0 && <XCircle size={14} weight='fill' color="var(--color-error)" />}
                              {payment.paymentStatus === 'pending' && <Clock size={14} weight='fill' color="var(--color-gray-50)" />}
                              {payment.amountRefunded > 0 && <ArrowCounterClockwise size={14} weight='fill' color="var(--color-gray-75)" />}
                              <div>
                                <p className="caption-bold">
                                  <span {...payment.amountRefunded > 0 && {
                                    style: { color: 'var(--color-gray-75)', textDecoration: 'line-through' },
                                  }}>
                                    {format.price.withCurrency(payment.amount, invoice.currency || DEFAULT_CURRENCY)}
                                  </span>
                                  {payment.amountRefunded > 0 && (
                                    <>
                                      {' '}
                                      {format.price.withCurrency(
                                        payment.amount - payment.amountRefunded,
                                        payment.creatorCurrency || DEFAULT_CURRENCY,
                                      )}
                                    </>
                                  )}
                                </p>
                                {payment.amountRefunded > 0 && <p className="caption" style={{ color: 'var(--color-gray-75)' }}>
                                  {`Refunded ${format.price.withCurrency(payment.amountRefunded, invoice.currency || DEFAULT_CURRENCY)}`}
                                </p>}
                                <p className="caption"><span>Payor:</span> {payment.person.name || payment.person.email || payment.person.id}</p>
                              </div>
                              <p className="caption"><span>{format.datetimeLocalTZ.withYear(payment.createdAt)}</span></p>
                            </li>
                          );
                        })}
                      </>}
                    </ul>}
                  </div>
                )}
              </>}
            {isConfirmationModalOpen && (
              <ConfirmationModal
                onClose={() => setConfirmationModalOpen(false)}
                variant="destructive"
                text="Are you sure you want to void this invoice? It will no longer be able to be paid."
                onSubmit={voidInvoice}
                cta="Void Invoice"
              />
            )}
            {isEditConfirmationModalOpen && (
              <EditInvoiceConfirmationModal onClose={() => setEditConfirmationModalOpen(false)} onSubmit={() => {
                setEditConfirmationModalOpen(false);
                setIsEditInvoiceModalOpen(true);
              }}/>
            )}
            {isEditInvoiceModalOpen && (
              <EditInvoiceModal invoiceData={invoice} toggleModal={editInvoiceModalToggle} handleSubmit={handleSubmitInvoice}/>
            )
            }
            {isPayOutsideModalOpen && (
              <PayInvoiceOutsideNoodleModal onClose={() => setPayOutsideModalOpen(false)} invoice={invoice} onSubmit={refetchData} />
            )}
            {isConfidoPaymentModalOpen && invoice.confidoPaymentLink && (
              <ConfidoPaymentModal confidoPaymentLink={invoice.confidoPaymentLink} onClose={() => setConfidoPaymentModalOpen(false)} />
            )}
            {isPaymentPlanModalOpen && (
              <PaymentPlanModal
                refetchData={refetchData}
                creatorSlug={creatorSlug}
                invoice={invoice}
                onClose={() => setPaymentPlanModalOpen(false)}
                onCancelPaymentPlan={showPaymentPlanAction && invoice.paymentPlan
                  ? () => {
                    setPaymentPlanModalOpen(false);
                    setCancelPaymentPlanModalOpen(true);
                  }
                  : undefined}
              />
            )}
            {isCancelPaymentPlanModalOpen && (
              <CancelPaymentPlanModal refetchData={refetchData} invoice={invoice} onClose={() => setCancelPaymentPlanModalOpen(false)} />
            )}
          </div>
        </div>
      </MainPanelSubLayout>
    </>
  );
};

export default InvoicePreview;
